<template>
  <div v-if="p">
    <section class="project-hero" :class="{ 'detail-hero': !p.heroAmplio }" aria-labelledby="project-title">
      <div class="project-hero-photo" :style="fondoHero"></div>
      <div class="hero-lines"></div>
      <div class="container hero-inner">
        <div class="reveal visible">
          <div class="breadcrumb">
            <a :href="RAIZ">Inicio</a><span>/</span><a :href="URL_PROYECTOS">Desarrollo</a><span>/</span><span>{{ p.nombre }}</span>
          </div>
          <div style="margin-top: 34px" class="eyebrow light">{{ p.ciudad }} · {{ p.regionNombre }}</div>
          <h1 id="project-title" v-html="p.titulo"></h1>
          <div class="hero-tag">{{ p.lema }}</div>
          <p class="subtitle">{{ p.subtitulo }}</p>
          <div class="hero-actions">
            <a href="#conoce" class="btn blue">Conocer el proyecto ↗</a>
            <a :href="p.accion.href" class="btn stroke">{{ p.accion.texto }}</a>
          </div>
        </div>
        <aside class="identity-card reveal visible" :aria-label="`Resumen de ${p.nombre}`">
          <div class="identity-top">
            <img v-if="p.sello" :src="p.sello" :alt="`Marca de ${p.nombre}`" @error="conRespaldo($event, p.selloRespaldo)">
            <div v-else class="identity-crest" aria-label="Monograma ilustrativo, no logo oficial">{{ p.monograma }}</div>
            <div><strong>{{ p.nombre }}</strong><small>{{ p.identidad.nota }}</small></div>
          </div>
          <div class="id-body">
            <span>{{ p.identidad.etiqueta }}</span>
            <p>{{ p.identidad.texto }}</p>
            <div class="id-stats">
              <div v-for="s in p.identidad.stats" :key="s.texto"><strong>{{ s.valor }}</strong><small>{{ s.texto }}</small></div>
            </div>
          </div>
        </aside>
      </div>
    </section>

    <div class="crumb-strip">
      <div class="container">
        <span>POLZ ASOCIADOS <span aria-hidden="true">/</span> DESARROLLO INMOBILIARIO</span>
        <a :href="URL_PROYECTOS">← Volver a todos los proyectos</a>
      </div>
    </div>

    <section id="conoce" class="section white">
      <div class="container intro-layout">
        <div v-reveal>
          <div class="eyebrow">01 / El proyecto</div>
          <h2 class="big-quote" style="margin-top: 25px" v-html="p.titular"></h2>
          <div v-if="p.kicker" class="detail-small-kicker">{{ p.kicker }}</div>
        </div>
        <div v-reveal class="copy">
          <p v-for="(texto, i) in p.parrafos" :key="i" v-html="texto"></p>
          <p v-if="p.nota" class="data-disclaimer">{{ p.nota }}</p>
        </div>
      </div>
    </section>

    <section class="numbers-band" aria-label="Datos principales del proyecto">
      <div class="container numbers">
        <div v-for="c in p.cifras" :key="c.texto" class="num"><strong>{{ c.valor }}</strong><small>{{ c.texto }}</small></div>
      </div>
    </section>

    <!-- El orden de las secciones cambia por ficha; la segunda va en blanco, como en el diseño. -->
    <template v-for="(s, i) in p.secciones" :key="s">
      <section v-if="s === 'atributos'" class="section" :class="{ white: i === 1 }">
        <div class="container">
          <div v-reveal class="section-head">
            <div>
              <div class="eyebrow">{{ numero(i) }} / {{ p.atributos.eyebrow }}</div>
              <h2>{{ p.atributos.titulo }}</h2>
            </div>
            <p>{{ p.atributos.texto }}</p>
          </div>
          <div class="features-grid">
            <article v-for="(a, j) in p.atributos.items" :key="a.titulo" v-reveal class="feature">
              <div class="symbol">{{ String(j + 1).padStart(2, '0') }}</div>
              <h3>{{ a.titulo }}</h3>
              <p>{{ a.texto }}</p>
            </article>
          </div>
        </div>
      </section>

      <section v-else-if="s === 'galeria'" id="galeria" class="section" :class="{ white: i === 1 }">
        <div class="container">
          <div v-reveal class="section-head">
            <div>
              <div class="eyebrow">{{ numero(i) }} / {{ p.galeria.eyebrow }}</div>
              <h2>{{ p.galeria.titulo }}</h2>
            </div>
            <p>{{ p.galeria.texto }}</p>
          </div>

          <div v-if="p.galeria.modo === 'miniaturas'" v-reveal class="gallery-grid">
            <button ref="principal" class="gallery-main" type="button" aria-label="Ampliar fotografía de galería" @click="abrirLightbox">
              <img :src="foto(actual.id, 1600, 85)" :alt="actual.alt" @error="conRespaldo($event, actual.respaldo)">
              <div class="gallery-caption"><span>{{ actual.leyenda }}</span><span>Ampliar ↗</span></div>
            </button>
            <div class="gallery-thumbs">
              <button
                v-for="(f, j) in p.galeria.fotos"
                :key="f.id"
                type="button"
                class="thumb"
                :class="{ active: j === seleccionada }"
                :aria-label="f.aria"
                @click="seleccionada = j"
              >
                <img :src="foto(f.id, 600, 78)" :alt="f.altMiniatura" @error="conRespaldo($event, f.respaldo)">
                <span>{{ f.miniatura }}</span>
              </button>
            </div>
          </div>
          <div v-else v-reveal class="detail-gallery">
            <figure v-for="f in p.galeria.fotos" :key="f.src">
              <img :src="f.src" :alt="f.alt" @error="conRespaldo($event, p.respaldo)">
              <figcaption>{{ f.leyenda }}</figcaption>
            </figure>
          </div>
          <p v-if="p.galeria.nota" class="ref-note">{{ p.galeria.nota }}</p>
        </div>
      </section>

      <section v-else-if="s === 'ubicacion'" id="ubicacion" class="section location-section">
        <div class="container location-grid">
          <div v-reveal class="location-copy">
            <div class="eyebrow">{{ numero(i) }} / Ubicación</div>
            <h2>{{ p.ubicacion.titulo }}</h2>
            <p v-for="texto in p.ubicacion.parrafos" :key="texto">{{ texto }}</p>
            <a v-if="p.ubicacion.boton" class="btn blue" :href="p.ubicacion.boton.href" target="_blank" rel="noopener">
              {{ p.ubicacion.boton.texto }}
            </a>
            <div v-if="p.ubicacion.chip" class="location-chip">{{ p.ubicacion.chip }}</div>
          </div>
          <div v-reveal class="location-art" :aria-label="p.ubicacion.aria">
            <div class="contours"></div>
            <div class="road"></div>
            <div class="pin"></div>
            <span class="map-village">{{ p.ubicacion.pueblo }}</span>
            <span v-if="p.ubicacion.ciudad" class="map-city">{{ p.ubicacion.ciudad }}</span>
            <div class="map-note">{{ p.ubicacion.nota }}</div>
          </div>
        </div>
      </section>

      <section v-else-if="s === 'video'" class="section" :class="{ white: i === 1 }">
        <div class="container">
          <div v-reveal class="video-box">
            <div>
              <div class="eyebrow">{{ numero(i) }} / Material audiovisual</div>
              <h2>{{ p.video.titulo }}</h2>
              <p>{{ p.video.texto }}</p>
              <a :href="p.video.url" target="_blank" rel="noopener" class="link-inline" style="color: #bde1ff">{{ p.video.enlace }}</a>
            </div>
            <a class="video-play" :href="p.video.url" target="_blank" rel="noopener" :aria-label="p.video.aria">▶</a>
          </div>
        </div>
      </section>

      <section v-else-if="s === 'relacionados'" class="section" :class="{ white: i === 1 }">
        <div class="container">
          <div v-reveal class="section-head">
            <div>
              <div class="eyebrow">{{ numero(i) }} / Continuar explorando</div>
              <h2>Otros proyectos de desarrollo.</h2>
            </div>
            <p>
              Todas las fichas y enlaces del portafolio pertenecen a este rediseño. Al seleccionar un
              proyecto no se abandona el nuevo sitio.
            </p>
          </div>
          <div v-reveal class="related-grid">
            <a v-for="r in p.relacionados" :key="r.slug" class="related-card" :href="urlFicha(r.slug)">
              <span class="related-photo" :style="fondoRelacionado(r)"></span>
              <span class="related-data">
                <small>{{ r.ciudad }} · {{ r.tipo }}</small>
                <strong>{{ r.nombre }}</strong>
                <span>Ver proyecto ↗</span>
              </span>
            </a>
          </div>
        </div>
      </section>
    </template>

    <section id="contacto" class="cta-strip">
      <div v-reveal class="container cta-grid">
        <div>
          <div class="eyebrow light">{{ p.cta.eyebrow }}</div>
          <h2>{{ p.cta.titulo }}</h2>
          <p>{{ p.cta.texto }}</p>
        </div>
        <div class="cta-buttons">
          <a :href="correo(p.asunto)" class="btn blue">Solicitar información ↗</a>
          <a :href="URL_PROYECTOS" class="btn stroke">{{ p.cta.secundario }}</a>
        </div>
      </div>
    </section>

    <div
      v-if="p.galeria.modo === 'miniaturas'"
      class="lightbox"
      :class="{ open: lightbox }"
      role="dialog"
      aria-modal="true"
      aria-label="Fotografía ampliada"
      :aria-hidden="String(!lightbox)"
      @click.self="cerrarLightbox"
    >
      <button ref="cerrar" class="close" type="button" aria-label="Cerrar imagen" @click="cerrarLightbox">×</button>
      <img v-if="lightbox" :src="foto(actual.id, 1600, 85)" :alt="actual.alt" @error="conRespaldo($event, actual.respaldo)">
      <p>Imagen referencial · No corresponde necesariamente al proyecto.</p>
    </div>
  </div>
</template>

<script>
import { RAIZ, URL_PROYECTOS, conRespaldo, correo, ficha, foto, respaldo, urlFicha } from './datos';
import reveal from './revelar';

export default {
  name: 'PzFicha',
  directives: { reveal },
  props: {
    proyecto: { type: String, required: true },
  },
  data() {
    return { RAIZ, URL_PROYECTOS, p: ficha(this.proyecto), seleccionada: 0, lightbox: false };
  },
  computed: {
    actual() {
      return this.p.galeria.fotos[this.seleccionada];
    },
    // Lomas de Lambert usa el fondo de la hoja; las demás, su foto con el respaldo detrás.
    fondoHero() {
      if (this.p.heroAmplio) return null;
      return {
        backgroundImage: 'linear-gradient(90deg,#06192ff0 0%,#06192fca 40%,#06192f57 100%),'
          + 'linear-gradient(0deg,#06192fce,transparent 60%),'
          + `url('${foto(this.p.foto, 1900, 82)}'),url('${respaldo(this.p.respaldo)}')`,
      };
    },
  },
  mounted() {
    document.addEventListener('keydown', this.tecla);
  },
  unmounted() {
    document.removeEventListener('keydown', this.tecla);
    document.body.style.overflow = '';
  },
  methods: {
    conRespaldo,
    correo,
    foto,
    urlFicha,
    numero(i) {
      return String(i + 2).padStart(2, '0');
    },
    fondoRelacionado(r) {
      return {
        backgroundImage: `linear-gradient(0deg,rgba(5,22,43,.92),rgba(5,22,43,.12)),url('${foto(r.foto, 1900, 82)}')`,
      };
    },
    abrirLightbox() {
      this.lightbox = true;
      document.body.style.overflow = 'hidden';
      this.$nextTick(() => this.$refs.cerrar.focus());
    },
    cerrarLightbox() {
      this.lightbox = false;
      document.body.style.overflow = '';
      const principal = this.$refs.principal;
      (Array.isArray(principal) ? principal[0] : principal).focus();
    },
    tecla(evento) {
      if (evento.key === 'Escape' && this.lightbox) this.cerrarLightbox();
    },
  },
};
</script>
